import React, { useState, useEffect, useCallback } from 'react';
import { Card, Col, Offcanvas, Row } from 'react-bootstrap';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import FacturationHeader from './factureHeader';
import { FacturationFilteringForm } from './facturationFilteringForm';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import entete from 'data/entetes';
import DetailsFacturation from './detailsFacturation/detailsFacturation';
import SubtleBadge from 'components/common/SubtleBadge';
import classNames from 'classnames';
import LignesModal from './detailsFacturation/lignesModel';
import { useSelector } from 'react-redux';

const columns2 = [
  {
    accessorKey: 'date',
    header: 'Date',
    accessorFn: row => {
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(row.date));
    },
    meta: {
      headerProps: { className: 'ps-2 text-900', style: { height: '46px' } },
      cellProps: {
        className: 'white-space-nowrap pe-5 ps-2 py-2',
      },
    },
  },
  {
    accessorKey: 'nfact',
    header: 'Num transaction',
    meta: {
      headerProps: { className: 'pe-1 white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap' },
    },
  },
  {
    accessorKey: 'factass',
    header: 'Facture associée',
    meta: {
      headerProps: { className: 'text-end text-900 ' },
      cellProps: {
        className: 'ps-2 text-end w-5 text-primary fw-semibold',
      },
    },
  },
  {
    accessorKey: 'numfacture',
    header: 'Opération',
    meta: {
      headerProps: { className: 'text-end text-900 ' },
      cellProps: {
        className: 'ps-2 text-end w-5 text-primary fw-semibold',
      },
    },
    cell: ({ row: { original } }) => {
      const { numfacture } = original;
      return (
        <SubtleBadge
          pill
          bg={numfacture !== '5' ? 'primary' : 'warning'}
          className="d-flex flex-center"
        >
          <p className="mb-0">{numfacture === '5' ? 'Retour' : 'Vente'}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'typeFacture',
    header: 'Type',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: {
        className: 'fs-9',
      },
    },
    cell: ({ row: { original } }) => {
      const { typeFacture } = original;
      return (
        <SubtleBadge
          pill
          bg={classNames({
            primary: typeFacture === 'BL',
            warning: typeFacture === 'BS',
            secondary: typeFacture === null,
          })}
          className="d-flex flex-center"
        >
          <p className="mb-0">{typeFacture === null ? 'N/F' : typeFacture}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'client',
    header: 'Client',
    meta: {
      headerProps: { className: 'text-900 text-start pe-9' },
      cellProps: {
        className: 'white-space-nowrap pe-9 text-start',
      },
    },
  },
  {
    accessorKey: 'mntdt',
    header: 'montant HTVA',
    meta: {
      headerProps: { className: 'text-900 text-start pe-3' },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start',
      },
    },
  },
  {
    accessorKey: 'tva6',
    header: 'TVA 7%',
    meta: {
      headerProps: { className: 'text-900 text-start pe-3' },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start',
      },
    },
    cell: ({ row: { original } }) => {
      return original.tva6 == '0.0000' ? '' : original.tva6;
    },
  },
  {
    accessorKey: 'tva12',
    header: 'TVA 12%',
    meta: {
      headerProps: { className: 'text-900 text-start pe-3' },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start',
      },
    },
    cell: ({ row: { original } }) => {
      return original.tva12 == '0.0000' ? '' : original.tva12;
    },
  },
  {
    accessorKey: 'tva18',
    header: 'TVA 19%',
    meta: {
      headerProps: { className: 'text-900 text-start pe-3' },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start',
      },
    },
    cell: ({ row: { original } }) => {
      return original.tva18 == '0.0000' ? '' : original.tva18;
    },
  },
  {
    accessorKey: 'sommeTva',
    header: 'Somme TVA',
    meta: {
      headerProps: { className: 'text-900 text-start pe-3' },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start ',
      },
    },
    cell: ({ row: { original } }) => {
      const tva18 = parseFloat(original.tva18 || 0);
      const tva12 = parseFloat(original.tva12 || 0);
      const tva6 = parseFloat(original.tva6 || 0);
      const tva = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
        useGrouping: false,
      }).format(tva18 + tva12 + tva6);
      // Calculate sum and format to 3 decimal places
      return tva == '0.000' ? '' : tva;
    },
  },
  {
    accessorKey: 'mntdtfin',
    header: 'Montant TTC',
    accessorFn: row => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(row.mntdtfin);
    },
    meta: {
      headerProps: {
        className: 'text-end white-space-nowrap text-900 pe-2 text-end',
      },
      cellProps: {
        className: 'white-space-nowrap pe-3 text-start ',
      },
    },
  },

  {
    accessorKey: 'magasin',
    header: 'Magasin',
    meta: {
      headerProps: { className: 'text-end text-900 w-2' },
      cellProps: { className: 'ps-2 text-end w-5' },
    },
    // cell: ({ row: { original } }) => {
    //   const { social } = original;
    //   return social ? (
    //     <a href="#!">{social}</a>
    //   ) : (
    //     <p className="mb-0 text-500">N/A</p>
    //   );
    // }
  },
];

const Contacts = () => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState([]);
  const [detailsFactures, setDetailsFactures] = useState({});
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [typeLignes, setTypeLignes] = useState('Séparées');
  const [showLignes, setShowLignes] = useState(false);
  const [NfactLignes, setNfactLignes] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [lignesLoading, setLignesLoading] = useState(false);
  const [entetesLoading, setEntetesLoading] = useState(false);
  const [changedSelection, setChangedSelection] = useState(false);
  const user = useSelector(state => state.auth.user);
  // States pour enregistrer les états des filtres
  // ----------------- Filtres ----------------- //

  const [filterState, setFilterState] = useState({
    selectedMagasin: user.magasin === 'Tous' ? 'Tous' : user.magasin,
    selectedChoix: 'Tous',
    selectedType: user.magasin == 'Tous' ? 'Tous' : 'BL',
    selectedTypeReglement: 'Tous',
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    typeTransaction: 'Tous',
  });
  // ----------------- Filtres ----------------- //

  const handleDetailsUpdate = () => {
    setLignesLoading(true);

    const selectedRows = table.getSelectedRowModel().rows;
    const allRows = table.getCoreRowModel().rows;

    // Get unselected rows
    const unselectedRows = allRows
      .filter(row => selectedRows.some(selected => selected.id === row.id))
      .map(row => row.original);
    const selectedFact = selectedRows.map(row => row.original.nfact);
    const selectedMagasin = unselectedRows[0].magasin;

    entete
      .getDetailsFactures({
        ...filterState,
        unselectedRows: [...selectedFact],
        typeLignes: typeLignes,
        magasin: selectedMagasin,
      })
      .then(data => {
        setDetailsFactures(data);
        setLignesLoading(false);
      });
  };

  const handleFilterUpdate = useCallback(conditions => {
    setFilterState(conditions);
  }, []);

  const fetchEntetes = async () => {
    setEntetesLoading(true);

    const filteredData =
      Object.keys(filterState).length > 0 ? await entete.getFactures(filterState) : {};
    setData(filteredData);
    setEntetesLoading(false);
  };

  const handleLignes = row => {
    setNfactLignes({
      nfact: row.original.nfact,
      magasin: row.original.magasin,
    });
    setShowLignes(true);
  };

  const table = useAdvanceTable({
    data: data,
    columns: columns2,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 100,
    selectionColumnWidth: 52,
  });

  useEffect(() => {
    setSelectedRows(table.getSelectedRowModel().rows);
    setChangedSelection(sel => !sel);
  }, [table.getSelectedRowModel().rows]);

  return (
    <>
      <LignesModal NfactLignes={NfactLignes} modalShow={showLignes} setModalShow={setShowLignes} />
      <Row className="gx-3">
        <Col>
          <AdvanceTableProvider {...table}>
            <Card>
              <Card.Header className="px-0">
                <FacturationHeader
                  handleShow={handleShow}
                  handleDetailsUpdate={handleDetailsUpdate}
                  setTypeLignes={setTypeLignes}
                  lignesLoading={lignesLoading}
                />
              </Card.Header>
              <Card.Body className="p-0" style={{ height: '500px', overflowY: 'auto' }}>
                <AdvanceTable
                  headerClassName="bg-body-tertiary align-middle"
                  rowClassName="btn-reveal-trigger align-middle"
                  tableProps={{
                    size: 'sm',
                    className: 'fs-10 mb-0 overflow-hidden',
                  }}
                  onRowClick={row => handleLignes(row)}
                  fixedHeader={true}
                />
              </Card.Body>
              <Card.Footer>
                <AdvanceTableFooter rowInfo navButtons />
              </Card.Footer>
            </Card>
          </AdvanceTableProvider>
        </Col>

        <Offcanvas show={show} onHide={handleClose} placement="end" className="dark__bg-card-dark">
          <Offcanvas.Header closeButton className="bg-body-tertiary">
            <h6 className="fs-9 mb-0 fw-semibold">Filter</h6>
          </Offcanvas.Header>
          <FacturationFilteringForm
            onFilterUpdate={handleFilterUpdate}
            filterState={filterState}
            fetchEntetes={fetchEntetes}
            entetesLoading={entetesLoading}
          />
        </Offcanvas>
      </Row>
      <Row className="gx-3 mt-3">
        <DetailsFacturation
          filteredFactures={detailsFactures}
          typeLignes={typeLignes}
          selectedRows={selectedRows}
          changedSelection={changedSelection}
        />
      </Row>
    </>
  );
};

export default Contacts;
